import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiTrash2 } from "react-icons/fi";
import { getGRNs, deleteGRN, createGRN } from "../../Services/GRNService";
import { getPurchaseOrders } from "../../Services/PurchaseOrderService";
import {
  CommonTable,
  Loader,
  Button,
  Modal,
  ConfirmDeleteModal,
  SearchableDropdown,
} from "../../Components/Common";
import {
  MESSAGES,
  GRN_LIST_COLUMNS,
  GRN_LIST_LABELS,
  APPROVAL_STATUS_STYLES,
  CONFIRM_DELETE_GRN,
  DRAFT,
} from "./constants";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { formatDateDDMMYYYY } from "../../Utils";
import { VIEW_GRN } from "../../routes";

const GrnList = () => {
  const navigate = useNavigate();
  const [grnList, setGrnList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [creating, setCreating] = useState(false);

  const getPaginatedGrns = async (finalParams, isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const response = await getGRNs(finalParams);
      if (response.status === 200) {
        const newGrns = response.data.results || [];
        setGrnList((prev) => (isInitial ? newGrns : [...prev, ...newGrns]));
        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
      if (isInitial) {
        setGrnList([]);
      }
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      } else {
        setPaginationLoading(false);
      }
    }
  };

  const loadMoreGrns = () => {
    if (pagination.offset >= pagination.count) return;
    getPaginatedGrns({ offset: pagination.offset, limit: pagination.limit });
  };

  // Searches locally-synced Purchase Orders by vendor name or PO number
  // (fetchFunction contract for SearchableDropdown)
  const searchPOs = async ({ search = "" } = {}) => {
    try {
      const response = await getPurchaseOrders({ search, limit: 20 });
      if (response.status === 200) {
        return response.data?.results || [];
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.PO_SEARCH_ERROR);
    }
    return [];
  };

  const openCreateModal = () => {
    setSelectedPO(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setSelectedPO(null);
    setCreating(false);
  };

  const handleCreateGrn = async () => {
    if (!selectedPO) return;
    setCreating(true);
    try {
      const response = await createGRN({ purchase_order: selectedPO.id });
      if (response.status === 201 || response.status === 200) {
        toast.success(MESSAGES.CREATE_SUCCESS);
        closeCreateModal();
        navigate(VIEW_GRN.replace(":grnId", response.data.id));
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.CREATE_ERROR);
        setCreating(false);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.CREATE_ERROR);
      setCreating(false);
    }
  };

  const handleRowClick = (row) => {
    navigate(VIEW_GRN.replace(":grnId", row.id));
  };

  const openDeleteConfirm = (row) => {
    setDeleteTarget(row);
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
    setDeleting(false);
  };

  const handleDeleteGrn = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteGRN(deleteTarget.id);
      if (response.status === 200 || response.status === 204) {
        toast.success(MESSAGES.DELETE_SUCCESS);
        setGrnList((prev) => prev.filter((grn) => grn.id !== deleteTarget.id));
        closeDeleteConfirm();
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.DELETE_ERROR;
        toast.error(msg);
        setDeleting(false);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.DELETE_ERROR;
      toast.error(msg);
      setDeleting(false);
    }
  };

  const renderFields = (key, row) => {
    switch (key) {
      case "created":
        return row.created
          ? `${new Date(row.created).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })} IST`
          : "-";
      case "approval_status": {
        const label =
          row.approval_status
            ?.toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ") || "-";
        const style =
          APPROVAL_STATUS_STYLES[row.approval_status] ||
          "bg-gray-100 text-gray-700";
        return (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${style}`}
          >
            {label}
          </span>
        );
      }
      case "actions":
        return (
          <div className="flex gap-4 items-center">
            {row.approval_status === DRAFT && (
              <div
                title="Delete"
                className="text-red-500 hover:text-red-600 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteConfirm(row);
                }}
              >
                <FiTrash2 size={18} />
              </div>
            )}
          </div>
        );
      default:
        return row[key] ?? "-";
    }
  };

  useEffect(() => {
    getPaginatedGrns({ offset: 0, limit: pagination.limit }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="px-4 my-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xl md:text-2xl text-primary font-semibold">
          GRN List
        </div>

        <Button
          className="bg-primary hover:bg-secondary text-white w-auto py-1.5 px-3 text-xs md:py-2 md:px-6 md:text-sm rounded-md shadow-md"
          onClick={openCreateModal}
        >
          + Create GRN
        </Button>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block">
        <div
          className="h-[68vh] overflow-x-auto overflow-y-auto md:border md:rounded-md"
          id="scrollableGrnDiv"
        >
          {initialLoading ? (
            <div className="flex justify-center items-center h-full py-10">
              <Loader size={4} />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={grnList.length}
              next={loadMoreGrns}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="scrollableGrnDiv"
            >
              <CommonTable
                tableColumns={GRN_LIST_COLUMNS}
                tableRows={grnList}
                columnLabels={GRN_LIST_LABELS}
                renderField={renderFields}
                rowClick={handleRowClick}
                customError={MESSAGES.NO_REQUESTS_AVAILABLE}
                className="text-center"
              />
              {paginationLoading && (
                <div className="flex justify-center py-4">
                  <Loader size={3} />
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden">
        {initialLoading ? (
          <div className="flex justify-center items-center py-10">
            <Loader size={4} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={grnList.length}
            next={loadMoreGrns}
            hasMore={pagination.offset < pagination.count}
            scrollableTarget="mobileGrnScroll"
          >
            <div className="h-[70vh] overflow-y-auto" id="mobileGrnScroll">
              <CommonTable
                tableColumns={GRN_LIST_COLUMNS}
                tableRows={grnList}
                columnLabels={GRN_LIST_LABELS}
                renderField={renderFields}
                rowClick={handleRowClick}
                customError={MESSAGES.NO_REQUESTS_AVAILABLE}
              />
              {paginationLoading && (
                <div className="flex justify-center py-4">
                  <Loader size={3} />
                </div>
              )}
            </div>
          </InfiniteScroll>
        )}
      </div>

      {/* Create GRN Modal */}
      <Modal
        isOpen={showCreateModal}
        title="Create GRN"
        footer={
          <>
            <button
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              onClick={closeCreateModal}
              disabled={creating}
            >
              Cancel
            </button>
            <Button
              className="w-auto py-2 px-6"
              onClick={handleCreateGrn}
              disabled={!selectedPO || creating}
            >
              {creating ? "Creating..." : "Create GRN"}
            </Button>
          </>
        }
      >
        <div className="space-y-4 mx-auto max-w-xs">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order<span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              fetchFunction={searchPOs}
              onSelect={(po) => setSelectedPO(po)}
              value={selectedPO}
              valueKey="id"
              labelKey="vendor_name"
              subLabelKey="zoho_po_number"
              showSubLabel={true}
              placeholder="Select Purchase Order"
              search_box_placeholder="Search by vendor or PO number..."
            />
          </div>

          {selectedPO && (
            <div className="border rounded-md p-3 bg-gray-50 text-sm">
              <div className="flex justify-between py-0.5">
                <span className="text-gray-500">Vendor</span>
                <span className="font-medium text-gray-700">
                  {selectedPO.vendor_name || "-"}
                </span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-gray-500">PO Number</span>
                <span className="font-medium text-gray-700">
                  {selectedPO.zoho_po_number || "-"}
                </span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-gray-500">PO Date</span>
                <span className="font-medium text-gray-700">
                  {formatDateDDMMYYYY(selectedPO.po_date) || "-"}
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        title="Delete GRN"
        message={CONFIRM_DELETE_GRN}
        onConfirm={handleDeleteGrn}
        onCancel={closeDeleteConfirm}
        loading={deleting}
      />
    </div>
  );
};

export default GrnList;
